// @flow

import type { Theme } from '../types/theme';
import defaultTheme from './default';

export const BRAND_PRIMARY = '#00457d';
export const BRAND_SECONDARY = '#fa6800';
export const BRAND_TERTIARY = '#3899df';

export const BRAND_PRIMARY_FROM = '#fda100';
export const BRAND_PRIMARY_TO = '#fa6800';

export const BRAND_PRIMARY_DARK = '#002e54';
export const BRAND_SECONDARY_DARK = '#da5b00';
export const BRAND_TERTIARY_DARK = '#0e71a8';

export const LIGHT_ORANGE = '#ffcaa4';
export const RED = '#ff0007';
export const LIGHT_RED = '#fedbdb';
export const GREEN = '#4ba82e';
export const LIGHT_GREEN = '#abef96';

export const WHITE = '#ffffff';
export const SILVER_LIGHT = '#f3f6f9';
export const SILVER = '#d4d8df';
export const GRAY_LIGHT = '#c2c5cb';
export const GRAY_DARK = '#727c8c';
export const BLACK = '#232323';

export const FONT_DEFAULT = '"Open Sans", Arial, sans-serif';
export const FONT_HEADING = '"Open Sans", Arial, sans-serif';
export const FONT_ACCENT = '"Open Sans", Arial, sans-serif';

export const FONT_WEIGHT_DEFAULT = 400;
export const FONT_WEIGHT_HEADING = 600;
export const FONT_WEIGHT_ACCENT = 400;

export const LIGHT_BLUE = '#cfe8f6';
export const CLOUDY_BLUE = '#bcd2db';

export const FONT_SIZE_DEFAULT = '1rem';
export const FONT_SIZE_TINY = '0.75rem';
export const FONT_SIZE_SMALL = '0.875rem';
export const FONT_SIZE_H1 = '2.5rem';
export const FONT_SIZE_H2 = '2.125rem';
export const FONT_SIZE_H3 = '1.75rem';
export const FONT_SIZE_H4 = '1.5rem';
export const FONT_SIZE_H5 = '1.125rem';
export const FONT_SIZE_H6 = '1rem';

export const BOX_SHADOW = '0 0 7px 0 rgba(0, 0, 0, 0.3)';
export const DROP_SHADOW = '0 0 5px rgba(0, 0, 0, 0.3)';
export const BORDER_RADIUS_DEFAULT = '6px';
export const BORDER_RADIUS_SMALL = '4px';
export const BORDER_COLOR = SILVER;

export const TEXT_DEFAULT = BLACK;
export const TEXT_SECONDARY = GRAY_DARK;
export const TEXT_TERTIARY = SILVER;
export const TEXT_HERO = WHITE;
export const TEXT_LIGHT = WHITE;
export const HEADING_DEFAULT = BRAND_PRIMARY;
export const BACKGROUND_DEFAULT = WHITE;
export const BACKGROUND_LIGHT = SILVER_LIGHT;
export const BACKGROUND_DARK = BLACK;
export const BACKGROUND_ERROR = LIGHT_RED;
export const BACKGROUND_SUCCESS = LIGHT_GREEN;

export const PRIMARY_BTN_SCHEME = {
  main: BRAND_PRIMARY,
  hover: BRAND_PRIMARY_DARK,
  active: BRAND_PRIMARY_DARK,
  contrast: WHITE,
  visited: BRAND_PRIMARY,
};

export const SECONDARY_BTN_SCHEME = {
  main: BRAND_SECONDARY,
  hover: BRAND_SECONDARY_DARK,
  active: BRAND_SECONDARY_DARK,
  contrast: WHITE,
  visited: BRAND_SECONDARY,
};

export const TERTIARY_BTN_SCHEME = {
  main: BRAND_TERTIARY,
  hover: BRAND_TERTIARY_DARK,
  active: BRAND_TERTIARY_DARK,
  contrast: WHITE,
  visited: BRAND_TERTIARY,
};

export const LIGHT_BTN_SCHEME = {
  main: WHITE,
  hover: SILVER_LIGHT,
  active: SILVER_LIGHT,
  contrast: WHITE,
  visited: WHITE,
};

export const MAIN_MENU_SCHEME = {
  main: BRAND_PRIMARY,
  hover: BRAND_SECONDARY,
  active: BRAND_SECONDARY,
  contrast: BRAND_TERTIARY,
  visited: BRAND_PRIMARY,
};
export const SUB_MENU_SCHEME = {
  main: WHITE,
  hover: WHITE,
  active: WHITE,
  contrast: BRAND_TERTIARY,
  visited: WHITE,
};

export const DISABLED_BTN_SCHEME = {
  main: GRAY_LIGHT,
  hover: GRAY_LIGHT,
  active: GRAY_LIGHT,
  contrast: WHITE,
  visited: GRAY_LIGHT,
};

/*
 * COLOR SCHEMES
 */
export const DEFAULT_SCHEME = {
  background: 'transparent',
  border: SILVER_LIGHT,
  text: TEXT_DEFAULT,
  heading: HEADING_DEFAULT,
  anchor: BRAND_SECONDARY,
  shadow: BOX_SHADOW,
};

export const DEFAULT_LIGHT_SCHEME = {
  background: 'transparent',
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const WHITE_SCHEME = {
  background: WHITE,
  border: SILVER_LIGHT,
  text: TEXT_DEFAULT,
  heading: HEADING_DEFAULT,
  anchor: BRAND_SECONDARY,
  shadow: BOX_SHADOW,
};

export const LIGHT_SCHEME = {
  background: SILVER_LIGHT,
  border: SILVER,
  text: TEXT_DEFAULT,
  subText: GRAY_DARK,
  heading: HEADING_DEFAULT,
  anchor: BRAND_SECONDARY,
  shadow: BOX_SHADOW,
};

export const DARK_SCHEME = {
  background: BLACK,
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const PRIMARY_SCHEME = {
  background: BRAND_PRIMARY,
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const SECONDARY_SCHEME = {
  background: BRAND_SECONDARY,
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const TERTIARY_SCHEME = {
  background: BRAND_TERTIARY,
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

const defaults = [{
  BRAND_PRIMARY,
  BRAND_SECONDARY,
  BRAND_SECONDARY_DARK,
  BRAND_TERTIARY,
  BRAND_PRIMARY_FROM,
  BRAND_PRIMARY_TO,
  WHITE,
  SILVER,
  GRAY: GRAY_LIGHT,
  GRAY_DARK,
  BLACK,
  ERROR: RED,
  SUCCESS: GREEN,
  FONT_DEFAULT,
  FONT_HEADING,
  FONT_ACCENT,
  FONT_WEIGHT_DEFAULT,
  FONT_WEIGHT_HEADING,
  FONT_WEIGHT_ACCENT,
  TEXT_DEFAULT,
  TEXT_SECONDARY,
  TEXT_TERTIARY,
  TEXT_HERO,
  TEXT_LIGHT,
  HEADING_DEFAULT,
  BACKGROUND_DEFAULT,
  BACKGROUND_LIGHT,
  BACKGROUND_DARK,
  BACKGROUND_ERROR,
  BACKGROUND_SUCCESS,
  FONT_SIZE_H1,
  FONT_SIZE_H2,
  FONT_SIZE_H3,
  FONT_SIZE_H4,
  FONT_SIZE_H5,
  FONT_SIZE_H6,
  FONT_SIZE_DEFAULT,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  BOX_SHADOW,
  DROP_SHADOW,
  BORDER_RADIUS_DEFAULT,
  BORDER_RADIUS_SMALL,
  BORDER_COLOR,
  PRIMARY_BTN_SCHEME,
  SECONDARY_BTN_SCHEME,
  TERTIARY_BTN_SCHEME,
  LIGHT_BTN_SCHEME,
  MAIN_MENU_SCHEME,
  LIGHT_ORANGE,
  DISABLED_BTN_SCHEME,
  DEFAULT_SCHEME,
  DEFAULT_LIGHT_SCHEME,
  WHITE_SCHEME,
  LIGHT_SCHEME,
  DARK_SCHEME,
  PRIMARY_SCHEME,
  SECONDARY_SCHEME,
  TERTIARY_SCHEME,
}];

const justlease: Theme = defaultTheme(defaults, {
  loader: {
    color: BRAND_SECONDARY,
  },
  table: {
    titleRow: {
      backgroundColor: SILVER_LIGHT,
    },
    titleCell: {
      backgroundColor: SILVER_LIGHT,
    },
  },
  pdbCompare: {
    subtitleColor: GRAY_DARK,
    removeIcon: GRAY_DARK,
  },
  assortment: {
    resultCount: {
      subtitleColor: GRAY_DARK,
    },
    filterBar: {
      backgroundColor: SILVER_LIGHT,
    },
    activeFilters: {
      backgroundColor: LIGHT_ORANGE,
    },
    carBlock: {
      comparisonLabelBackground: SILVER_LIGHT,
    },
  },
  form: {
    password: {
      stages: {
        weak: RED,
        acceptable: BRAND_SECONDARY_DARK,
        strong: GREEN,
      },
    },
  },
});

export default justlease;
